import { FC, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Button } from '@/components/ui/button';
import AnalyticsIcon from '@/components/icons/AnalyticsIcon';
import CalendarIcon from '@/components/icons/CalendarIcon';
import DashboardCircleIcon from '@/components/icons/DashboardCircleIcon';
import InfoCircle from '@/components/icons/InfoCircle';
import PackageIcon from '@/components/icons/PackageIcon';
import ApartmentsDetailsBasic from '@/features/apartments/components/ApartmentsDetails/Basic/ApartmentsDetailsBasic';
import ApartmentsDetailsAmenities from '@/features/apartments/components/ApartmentsDetails/Amenities/ApartmentsDetailsAmenities';
import ApartmentsDetailsCalendar from '@/features/apartments/components/ApartmentsDetails/ApartmentsDetailsCalendar';
import ApartmentsDetailsFinances from '@/features/apartments/components/ApartmentsDetails/ApartmentsDetailsFinances';
import OrdersInfo from '@/features/orders/components/OrdersInfo/OrdersInfo';
import { GetApartmentDetailsResponseDto } from '@/features/apartments/types/apartments-dto';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { t } from 'i18next';
import { OwnerPermissionsResponseDto } from '@/features/owners/types/owners-dto';
import AgreementIcon from '@/components/icons/AgreementIcon';
import UserIcon from '@/components/icons/UserIcon';
import AgreementsList from '@/features/trustees/components/agreements/AgreementsList';
import TrusteesList from '@/features/trustees/components/TrusteesList';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { usePermissionsListApi } from '@/features/settings/hooks/usePermissionsListApi';
import { Roles } from '@/@types/auth.types';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

enum ApartmentDetailsListButtons {
	BasicDetails,
	Amenities,
	Calendar,
	Finances,
	Orders,
	Agreements,
	Trustees,
}

type Props = {
	data: GetApartmentDetailsResponseDto;
	isLoading?: boolean;
};

const ApartmentDetailsList: FC<Props> = ({ data, isLoading }) => {
	const [activeDetail, setActiveDetail] = useState(
		ApartmentDetailsListButtons.BasicDetails,
	);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const userRole = useUserRole();
	const { permissionsListData: permissions } = usePermissionsListApi({
		role: userRole,
	});
	const { ordersEnabled } = useMultiCurrencyStorage();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		dragFree: true,
	});

	const clickHandler = (index: number) => {
		setActiveDetail(index);
		if (emblaApi) {
			emblaApi.scrollTo(index);
		}
	};

	const apartmentDetailsButtonsList = [
		{
			type: ApartmentDetailsListButtons.BasicDetails,
			name: t('main.apartments.components.details.buttons.basicDetails'),
			icon: InfoCircle,
			enabled: () => true,
			component: (
				<ApartmentsDetailsBasic
					cadastralCode={data.cadastralCode}
					squareness={data.squareness}
					electricityId={data.electricityId}
					waterId={data.waterId}
					gasId={data.gasId}
					internetId={data.internetId}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: ApartmentDetailsListButtons.Amenities,
			name: t('main.apartments.components.details.buttons.amenities'),
			icon: DashboardCircleIcon,
			enabled: () => true,
			component: (
				<ApartmentsDetailsAmenities
					amenities={data.amenities}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: ApartmentDetailsListButtons.Calendar,
			name: t('main.apartments.components.details.buttons.calendar'),
			icon: CalendarIcon,
			enabled: (permissions?: OwnerPermissionsResponseDto) =>
				userRole === Roles.admin ? true : permissions?.showCalendar,
			component: (
				<ApartmentsDetailsCalendar
					id={data.id}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: ApartmentDetailsListButtons.Finances,
			name: t('main.apartments.components.details.buttons.finances'),
			icon: AnalyticsIcon,
			enabled: (permissions?: OwnerPermissionsResponseDto) =>
				userRole === Roles.admin ? true : permissions?.showFinances,
			component: (
				<ApartmentsDetailsFinances
					apartmentId={data.id}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: ApartmentDetailsListButtons.Orders,
			name: t('main.apartments.components.details.buttons.orders'),
			icon: PackageIcon,
			enabled: () => ordersEnabled,
			component: <OrdersInfo isMobile={isMobile} />,
		},
		{
			type: ApartmentDetailsListButtons.Agreements,
			name: t('main.apartments.components.details.buttons.agreements'),
			icon: AgreementIcon,
			enabled: () => AppConfig.features.showApartmentAgreements,
			component: <AgreementsList isMobile={isMobile} />,
		},
		{
			type: ApartmentDetailsListButtons.Trustees,
			name: t('main.apartments.components.details.buttons.trustees'),
			icon: UserIcon,
			enabled: () => AppConfig.features.showApartmentTrustees,
			component: <TrusteesList isMobile={isMobile} />,
		},
	];

	const visibleDetails = apartmentDetailsButtonsList.filter((element) => {
		return element.enabled(permissions);
	});

	return (
		<div className={'flex-1 flex flex-col gap-6'}>
			<div
				className={cn(
					'-mx-6 px-6 overflow-hidden',
					isMobile && '-mx-4 px-4',
				)}
				ref={emblaRef}>
				<div className='flex gap-2 touch-pan-y'>
					{visibleDetails.map(({ type, name, icon: Icon }) => (
						<Button
							key={type}
							variant={
								activeDetail === type ? 'default' : 'outline'
							}
							className={cn(
								`hover:shadow-none ${
									activeDetail === type
										? ''
										: 'hover:bg-primary-color-50'
								} `,
								isMobile && 'h-9 px-3.5 py-2',
							)}
							onClick={() => clickHandler(type)}>
							<Icon
								className={`${
									activeDetail === type
										? 'stroke-popover-10'
										: 'stroke-popover-1000'
								}`}
							/>
							{name}
						</Button>
					))}
				</div>
			</div>
			<div className='flex-1'>
				{
					visibleDetails.find(
						(element) => element.type === activeDetail,
					)?.component
				}
			</div>
		</div>
	);
};

export default ApartmentDetailsList;
